  import { FC, useState, useMemo, useCallback } from "react";
import styles from "./styles.module.css";
import { useGetCurrencyConversionsQuery } from "@entities/Shop/api/shopApiSlice";
import { useGetUserInfoQuery } from "@entities/User/api/UserInformationSlice";
import { EmailVerificationModal } from "./EmailVerificationModal";
import { ConversionCard } from "./components/ConversionCard";
import { Loader } from "@shared/ui/Loader";

export const CashShop: FC = () => {
  const { data, isLoading } = useGetCurrencyConversionsQuery();
  const { data: userInfo } = useGetUserInfoQuery();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const { pylConversions, gCoinConversions } = useMemo(() => ({
    pylConversions: data?.currencyConversions
      .filter(conversion => conversion.toCurrency === "PYL")
      .sort((a, b) => a.fromAmount - b.fromAmount) || [],
    gCoinConversions: data?.currencyConversions
      .filter(conversion => conversion.toCurrency === "G_COIN")
      .sort((a, b) => a.fromAmount - b.fromAmount) || []
  }), [data?.currencyConversions]);

  const handleButtonClick = useCallback((paymentLink: string) => {
    if (!userInfo?.email) {
      setIsEmailModalOpen(true);
      return;
    }
    
    if (userInfo?.role !== "guest") {
      window.open(paymentLink, "_blank");
    }
  }, [userInfo]);

  const handleEmailVerificationSuccess = useCallback(() => {
    setIsEmailModalOpen(false);
  }, []);

  const getButtonStyle = useCallback((currency: string, isLast: boolean) => {
    if (isLast) {
      return { 
        background: "#888888", 
        opacity: 0.7,
        cursor: "not-allowed"
      };
    }
    
    return currency === "G_COIN" 
      ? { background: "#f2eb56" }
      : { background: "#56f2a4" };
  }, []);

  const renderConversions = useCallback((conversions: typeof pylConversions, currencyType: "PYL" | "G_COIN") => {
    if (isLoading) {
      return <Loader fullscreen={false} size="large" />;
    }

    if (conversions.length === 0) {
      return <p className={styles.comingSoon}>Coming soon</p>;
    }

    return (
      <div className={styles.conversionsGrid}>
        {conversions.map((conversion, index, array) => {
          const isLastCard = index === array.length - 1;
          
          return (
            <ConversionCard
              key={conversion.id}
              conversion={conversion}
              isLastCard={isLastCard}
              currencyType={currencyType}
              onButtonClick={handleButtonClick}
              getButtonStyle={getButtonStyle}
            />
          );
        })}
      </div>
    );
  }, [isLoading, handleButtonClick, getButtonStyle]);

  return (
    <div className={styles.container}>
      <EmailVerificationModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSuccess={handleEmailVerificationSuccess}
      />
      <div className={styles.cashContainer}>
        <div className={styles.PylContainer}>
          {renderConversions(pylConversions, "PYL")}
        </div>
        <div className={styles.gContainer}>
          {renderConversions(gCoinConversions, "G_COIN")}
        </div>
      </div>
    </div>
  );
};
